import { useEffect, useMemo, useState } from 'react';
import { Search, CheckSquare, Square, Copy, Loader } from 'lucide-react';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useAuthReady } from '../../../hooks/useAuthReady';
import { Organization } from '../../../types/index';
import { createDataSyncRun, DataSyncRun } from '../../../utils/dataSyncApi';
import { SyncProgressBar } from './SyncProgressBar';

interface OrganizationsSyncTabProps {
  activeRun: DataSyncRun | null;
  onRunStarted: (runId: string) => void;
  runDisabled: boolean;
}

export function OrganizationsSyncTab({
  activeRun,
  onRunStarted,
  runDisabled,
}: OrganizationsSyncTabProps) {
  const authReady = useAuthReady(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [includeRecipes, setIncludeRecipes] = useState(true);
  const [includeCookbook, setIncludeCookbook] = useState(true);
  const [includeLinkedIngredients, setIncludeLinkedIngredients] = useState(true);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authReady) return;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'organizations')));
        setOrganizations(
          snap.docs.map((d) => ({ id: d.id, ...d.data() } as Organization))
        );
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [authReady]);

  const filtered = useMemo(
    () =>
      organizations.filter((o) =>
        (o.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [organizations, searchTerm]
  );

  const toggle = (id: string) => {
    setSelectAll(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    setSelectAll(false);
    setSelected(new Set(filtered.map((o) => o.id)));
  };

  const handleSelectAllInDb = () => {
    setSelectAll(true);
    setSelected(new Set());
  };

  const handleClear = () => {
    setSelectAll(false);
    setSelected(new Set());
  };

  const selectionCount = selectAll ? organizations.length : selected.size;

  const handleCopy = async () => {
    if (selectionCount === 0) return;
    if (
      !window.confirm(
        `Copier ${selectAll ? 'TOUTES' : selectionCount} organisation(s) vers la préprod ? Les documents existants seront écrasés.`
      )
    ) {
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const { runId } = await createDataSyncRun({
        type: 'organizations',
        ids: selectAll ? 'all' : [...selected],
        options: { includeRecipes, includeCookbook, includeLinkedIngredients },
      });
      onRunStarted(runId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-menioo-green" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SyncProgressBar run={activeRun} />

      <div className="flex flex-wrap items-center gap-4 bg-white border border-gray-200 rounded-lg p-4">
        <label className="inline-flex items-center gap-2 text-sm text-menioo-navy">
          <input
            type="checkbox"
            checked={includeRecipes}
            onChange={(e) => setIncludeRecipes(e.target.checked)}
            className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
          />
          Recettes de l’org
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-menioo-navy">
          <input
            type="checkbox"
            checked={includeCookbook}
            onChange={(e) => setIncludeCookbook(e.target.checked)}
            className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
          />
          Cookbook
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-menioo-navy">
          <input
            type="checkbox"
            checked={includeLinkedIngredients}
            onChange={(e) => setIncludeLinkedIngredients(e.target.checked)}
            className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
          />
          Ingrédients liés
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 text-sm focus:border-menioo-green focus:ring-menioo-green"
          />
        </div>
        <button
          type="button"
          onClick={handleSelectAllFiltered}
          className="text-sm text-menioo-navy hover:underline"
        >
          Sélectionner filtrés ({filtered.length})
        </button>
        <button
          type="button"
          onClick={handleSelectAllInDb}
          className="text-sm text-menioo-navy hover:underline"
        >
          Tout ({organizations.length})
        </button>
        <button type="button" onClick={handleClear} className="text-sm text-gray-500 hover:underline">
          Effacer
        </button>
        <button
          type="button"
          onClick={handleCopy}
          disabled={runDisabled || starting || selectionCount === 0}
          className="ml-auto inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
        >
          {starting ? (
            <Loader className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Copy className="h-4 w-4 mr-2" />
          )}
          Copier ({selectAll ? 'tout' : selectionCount})
        </button>
      </div>

      {selectAll && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Mode « tout » : toutes les organisations de la base source seront copiées.
        </p>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg divide-y max-h-[28rem] overflow-y-auto">
        {filtered.map((org) => {
          const isSelected = selectAll || selected.has(org.id);
          return (
            <button
              key={org.id}
              type="button"
              onClick={() => toggle(org.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
            >
              {isSelected ? (
                <CheckSquare className="h-4 w-4 text-menioo-green flex-shrink-0" />
              ) : (
                <Square className="h-4 w-4 text-gray-400 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-menioo-navy truncate">{org.name}</div>
                <div className="text-xs text-gray-400 truncate">{org.id}</div>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-gray-500">Aucune organisation</p>
        )}
      </div>
    </div>
  );
}
